import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { formatApiError, setAuthToken } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(false);
      // No valid session — drop any stale Bearer fallback we may have stored.
      setAuthToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      // api.js response interceptor already persists data.access_token; this
      // is just a belt-and-braces guarantee in case the interceptor is bypassed.
      if (data?.access_token) setAuthToken(data.access_token);
      // If a first-login forced password change is required, keep the login
      // payload as-is so PasswordChangeGate can render immediately. Otherwise
      // hydrate from /auth/me so computed fields (is_super_admin, etc.) that
      // are NOT in the login response are populated on the client. Single
      // source of truth for user identity stays on the server side.
      if (data?.force_password_change) {
        setUser(data);
      } else {
        try {
          const me = await api.get('/auth/me');
          setUser(me.data);
        } catch {
          // /me failure is non-fatal — fall back to the login payload so the
          // session isn't lost.
          setUser(data);
        }
      }
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); }
    catch (e) { console.error('logout request failed (clearing local state anyway)', e); }
    setAuthToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, googleLogin, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
